import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Bell, Settings2 } from "lucide-react";
import { toast } from "sonner";

/**
 * NotificationBell — polls unread notifications every 30s. Shows a red-dot badge and
 * a small dropdown of the last 10 items. Toasts new items when they arrive. Includes
 * an inline Alert Preferences panel so users can silence specific stop-event types.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [seenIds, setSeenIds] = useState(new Set());
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ stop_loss: true, take_profit: true, trailing_stop: true });
  const [savingPref, setSavingPref] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      const list = data.notifications || [];
      if (seenIds.size > 0) {
        for (const n of list) {
          if (!seenIds.has(n.id) && !n.read) {
            toast(n.title, { description: n.message, duration: 8000 });
          }
        }
      }
      setSeenIds(new Set(list.map((n) => n.id)));
      setItems(list);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  };

  const loadPrefs = async () => {
    try {
      const { data } = await api.get("/notifications/prefs");
      setPrefs({ stop_loss: !!data.stop_loss, take_profit: !!data.take_profit, trailing_stop: !!data.trailing_stop });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load(); loadPrefs();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const markAllRead = async () => {
    try { await api.post("/notifications/mark-read"); setUnread(0); load(); } catch {}
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSavingPref(key);
    try {
      await api.patch("/notifications/prefs", next);
      toast.success(`${key.replace("_", " ")} alerts ${next[key] ? "on" : "off"}`);
    } catch (e) {
      setPrefs(prefs); // revert
      toast.error("Couldn't update preferences");
    }
    setSavingPref(null);
  };

  const PREF_LABELS = {
    stop_loss: "Stop-loss hits",
    take_profit: "Take-profit hits",
    trailing_stop: "Trailing stops",
  };

  return (
    <div className="relative" data-testid="notif-bell-wrapper">
      <button onClick={() => setOpen(!open)}
              data-testid="notif-bell-btn"
              className="relative p-2 rounded-full hover:bg-secondary/60 transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span data-testid="notif-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold font-mono-tab flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => { setOpen(false); setShowPrefs(false); }} className="fixed inset-0 z-40" />
          <div data-testid="notif-dropdown"
               className="absolute right-0 top-9 z-50 w-80 rounded-xl border border-border bg-card shadow-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider">Alerts</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowPrefs(!showPrefs)}
                        data-testid="notif-prefs-toggle"
                        title="Alert preferences"
                        className={`p-1 rounded transition-colors ${showPrefs ? "text-emerald-400 bg-emerald-500/10" : "text-muted-foreground hover:text-foreground"}`}>
                  <Settings2 className="w-3 h-3" />
                </button>
                {unread > 0 && (
                  <button onClick={markAllRead}
                          data-testid="notif-mark-all-read"
                          className="text-[10px] text-muted-foreground hover:text-foreground underline">
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            {showPrefs && (
              <div className="px-3 py-2 border-b border-border/60 bg-secondary/20" data-testid="notif-prefs-panel">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  Fire notifications for
                </div>
                <div className="space-y-1.5">
                  {Object.keys(PREF_LABELS).map((k) => (
                    <label key={k} className="flex items-center justify-between text-[11px] cursor-pointer"
                           data-testid={`notif-pref-${k}`}>
                      <span className="text-foreground">{PREF_LABELS[k]}</span>
                      <button
                        type="button"
                        onClick={() => togglePref(k)}
                        disabled={savingPref === k}
                        aria-pressed={prefs[k]}
                        className={`relative w-8 h-4 rounded-full transition-colors ${
                          prefs[k] ? "bg-emerald-500" : "bg-secondary border border-border"
                        }`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                          prefs[k] ? "translate-x-4" : "translate-x-0.5"
                        }`} />
                      </button>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground text-center italic">
                  Nothing to report. Aanar will ping you when a stop, take-profit, or trail fires.
                </div>
              ) : items.map((n) => (
                <div key={n.id}
                     data-testid={`notif-${n.type}`}
                     className={`px-3 py-2 border-b border-border/40 ${!n.read ? "bg-emerald-500/[0.04]" : ""}`}>
                  <div className="text-xs font-semibold flex items-center gap-1">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                    {n.title}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{n.message}</div>
                  <div className="text-[10px] font-mono-tab text-muted-foreground/70 mt-0.5">
                    {new Date(n.created_at).toLocaleString(undefined, {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
